import type {
  ConversationCreate,
  ConversationOut,
  ConversationPatch,
  ConversationSummary,
  MessageCreate,
  MessageOut,
  RunOut,
} from '../types/api';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type TokenGetter = () => Promise<string | undefined>;
type AuthErrorHandler = () => void;

async function request<T>(
  path: string,
  init: RequestInit,
  getToken: TokenGetter,
  onAuthError: AuthErrorHandler,
): Promise<T> {
  const token = await getToken();
  const headers = new Headers(init.headers);
  headers.set('Content-Type', 'application/json');
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  if (!res.ok) {
    if (res.status === 401) {
      onAuthError();
    }
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      body = await res.text().catch(() => undefined);
    }
    throw new ApiError(res.status, `HTTP ${res.status} on ${path}`, body);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export function createApi(getToken: TokenGetter, onAuthError: AuthErrorHandler) {
  return {
    listConversations: (includeArchived = false) =>
      request<ConversationSummary[]>(
        `/v1/conversations?include_archived=${includeArchived}`,
        { method: 'GET' },
        getToken,
        onAuthError,
      ),

    createConversation: (body: ConversationCreate) =>
      request<ConversationOut>(
        '/v1/conversations',
        { method: 'POST', body: JSON.stringify(body) },
        getToken,
        onAuthError,
      ),

    getConversation: (id: string, opts?: { offset?: number; limit?: number }) => {
      const params = new URLSearchParams();
      if (opts?.offset !== undefined) params.set('offset', String(opts.offset));
      if (opts?.limit !== undefined) params.set('limit', String(opts.limit));
      const q = params.toString();
      return request<ConversationOut>(
        `/v1/conversations/${id}${q ? `?${q}` : ''}`,
        { method: 'GET' },
        getToken,
        onAuthError,
      );
    },

    patchConversation: (id: string, body: ConversationPatch) =>
      request<ConversationSummary>(
        `/v1/conversations/${id}`,
        { method: 'PATCH', body: JSON.stringify(body) },
        getToken,
        onAuthError,
      ),

    deleteConversation: (id: string) =>
      request<void>(
        `/v1/conversations/${id}`,
        { method: 'DELETE' },
        getToken,
        onAuthError,
      ),

    appendMessage: (id: string, body: MessageCreate) =>
      request<MessageOut>(
        `/v1/conversations/${id}/messages`,
        { method: 'POST', body: JSON.stringify(body) },
        getToken,
        onAuthError,
      ),

    getRun: (runId: string) =>
      request<RunOut>(
        `/v1/runs/${runId}`,
        { method: 'GET' },
        getToken,
        onAuthError,
      ),

    cancelRun: (runId: string) =>
      request<RunOut>(
        `/v1/runs/${runId}/cancel`,
        { method: 'POST' },
        getToken,
        onAuthError,
      ),
  };
}

export type Api = ReturnType<typeof createApi>;
export { API_BASE };
